import React from 'react';
import { format } from 'date-fns';
import { WASTE_CATEGORIES, getCategoryColor } from '../config/wasteCategories';
import { FaTimes, FaMapMarkerAlt, FaClock, FaUser, FaCamera, FaVideo, FaMicrophone, FaPen } from 'react-icons/fa';
import './WasteDetailModal.css';

const anonymizeUsername = (name) => {
  if (!name) return 'Anonymous User';
  const parts = name.split(/[@\s]/);
  const firstPart = parts[0];
  if (firstPart.length <= 3) return firstPart + '***';
  return firstPart.substring(0, 3) + '***';
};

const WasteDetailModal = ({ isOpen, onClose, wasteReport }) => {
  if (!isOpen || !wasteReport) return null;

  const category = WASTE_CATEGORIES[wasteReport.category];
  const categoryColor = getCategoryColor(wasteReport.category);
  const evidenceTypes = wasteReport.evidenceType?.split(',') || [];

  const getEvidenceIcon = (type) => {
    switch (type) {
      case 'photo': return <FaCamera />;
      case 'video': return <FaVideo />;
      case 'audio': return <FaMicrophone />;
      case 'text': return <FaPen />;
      default: return null;
    }
  };

  return (
    <div className="waste-detail-overlay" onClick={onClose}>
      <div className="waste-detail-modal" onClick={e => e.stopPropagation()}>
        <button className="detail-close-btn" onClick={onClose}>
          <FaTimes />
        </button>

        <div className="detail-header" style={{ backgroundColor: categoryColor }}>
          <div className="detail-category-icon">
            {category?.icon || '🗑️'}
          </div>
          <div className="detail-header-info">
            <h2>{category?.name || 'Waste Report'}</h2>
            <span className="detail-waste-type">{wasteReport.wasteType}</span>
          </div>
        </div>

        <div className="detail-body">
          <div className="detail-section">
            <div className="detail-item">
              <FaClock className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Reported On</span>
                <span className="detail-value">
                  {format(new Date(wasteReport.timestamp), 'MMMM dd, yyyy \'at\' HH:mm')}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <FaUser className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Reported By</span>
                <span className="detail-value">
                  {anonymizeUsername(wasteReport.reportedBy)}
                </span>
              </div>
            </div>

            <div className="detail-item">
              <FaMapMarkerAlt className="detail-icon" />
              <div className="detail-content">
                <span className="detail-label">Location</span>
                <span className="detail-value coordinates">
                  {wasteReport.location.lat.toFixed(6)}, {wasteReport.location.lng.toFixed(6)}
                </span>
                {wasteReport.location.accuracy && (
                  <span className="detail-accuracy">
                    Accuracy: {wasteReport.location.accuracy.toFixed(0)}m
                  </span>
                )}
              </div>
            </div>

            <div className="detail-item">
              <div className="detail-icon evidence-icon-container">
                {evidenceTypes.map((type, i) => (
                  <span key={i}>{getEvidenceIcon(type.trim())}</span>
                ))}
              </div>
              <div className="detail-content">
                <span className="detail-label">Evidence Types</span>
                <span className="detail-value">
                  {evidenceTypes.map(t => t.trim().charAt(0).toUpperCase() + t.trim().slice(1)).join(', ')}
                </span>
              </div>
            </div>
          </div>

          {wasteReport.description && (
            <div className="detail-description">
              <h4>Description</h4>
              <p>{wasteReport.description}</p>
            </div>
          )}

          {wasteReport.photoURL && (
            <div className="detail-media">
              <h4>Photo Evidence</h4>
              <img src={wasteReport.photoURL} alt="Waste evidence" />
            </div>
          )}

          {wasteReport.videoURL && (
            <div className="detail-media">
              <h4>Video Evidence</h4>
              <video src={wasteReport.videoURL} controls />
            </div>
          )}

          {wasteReport.audioURL && (
            <div className="detail-media">
              <h4>Audio Evidence</h4>
              <audio src={wasteReport.audioURL} controls />
            </div>
          )}

          <div className="detail-status">
            <span className={`status-badge ${wasteReport.status}`}>
              {wasteReport.status === 'cleaned' ? 'Cleaned Up' : 'Pending Cleanup'}
            </span>
          </div>

          {wasteReport.cleanupEvidence && (
            <div className="cleanup-evidence">
              <h4>Cleanup Evidence</h4>
              <p>Cleaned by: {anonymizeUsername(wasteReport.cleanupEvidence.submittedBy)}</p>
              <p>Date: {format(new Date(wasteReport.cleanupEvidence.timestamp), 'MMM dd, yyyy HH:mm')}</p>
              {wasteReport.cleanupEvidence.photoURL && (
                <img src={wasteReport.cleanupEvidence.photoURL} alt="Cleanup evidence" />
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WasteDetailModal;
